import { apiClient } from '@/api/client'
import { endpoints } from '@/api/endpoints'
import {
  Voucher,
  VoucherType,
  VoucherListItem,
  VoucherListResponse,
  VoucherLine,
  ApiResponse,
} from '@/api/types'
import { appendSortDir, type ListSortDir } from '@/lib/listSort'

/**
 * Voucher/General Entry service
 * Handles all voucher-related API operations
 */
export class VoucherService {
  /**
   * Get all voucher types
   */
  async getVoucherTypes(): Promise<VoucherType[]> {
    try {
      const url = endpoints.vouchers.getVoucherTypes
      console.log('🔵 [getVoucherTypes] Calling:', url)
      const response = await apiClient.get<ApiResponse<VoucherType[]> | VoucherType[] | any>(url)
      console.log('🔵 [getVoucherTypes] Full Response:', response)
      console.log('🔵 [getVoucherTypes] Response Data:', response.data)
      
      let dataArray: any[] = []
      
      // Handle different response formats
      if (Array.isArray(response.data)) {
        dataArray = response.data
      } else if (response.data && typeof response.data === 'object' && 'data' in response.data) {
        const data = (response.data as any).data
        if (Array.isArray(data)) {
          dataArray = data
        }
      } else if (Array.isArray(response)) {
        dataArray = response
      }
      
      console.log('🔵 [getVoucherTypes] Data Array:', dataArray)
      
      // Map API response to VoucherType format
      // API returns: { id, lookupCodeId, lookupNameEn, lookupNameAr, ... }
      // We need: { id, name, nameEn, nameAr }
      const mappedData: VoucherType[] = dataArray.map((item: any) => ({
        id: item.id || item.lookupCodeId || 0,
        name: item.lookupNameEn || item.nameEn || item.name || item.lookupNameAr || item.nameAr || '',
        nameEn: item.lookupNameEn || item.nameEn || '',
        nameAr: item.lookupNameAr || item.nameAr || '',
        code: item.code || item.lookupCode || '',
      }))
      
      console.log('🔵 [getVoucherTypes] Mapped Data:', mappedData)
      console.log('🔵 [getVoucherTypes] Returning:', mappedData.length, 'items')
      
      return mappedData
    } catch (error: any) {
      console.error('❌ [getVoucherTypes] Error:', error)
      console.error('❌ [getVoucherTypes] Error Response:', error.response)
      throw error
    }
  }

  /**
   * Get paginated list of vouchers for a branch
   */
  async getAllVouchers(
    branchId: number,
    pageNumber: number,
    pageSize: number,
    sortDir: ListSortDir = 'desc'
  ): Promise<VoucherListResponse> {
    const url = appendSortDir(endpoints.vouchers.getAll(branchId, pageNumber, pageSize), sortDir)
    const response = await apiClient.get<ApiResponse<VoucherListResponse> | VoucherListResponse | any>(url)
    const payload = response.data

    // Helper to map vouchers array
    const mapVouchers = (vouchers: any[]): VoucherListItem[] => {
      return vouchers.map(v => this.mapVoucherListItem(v))
    }

    if (payload && typeof payload === 'object') {
      if ('success' in payload && 'data' in payload) {
        const data = payload.data
        if (Array.isArray(data)) {
          return {
            totalRecords: payload.totalRecords ?? data.length,
            pageNumber: payload.pageNumber ?? pageNumber,
            pageSize: payload.pageSize ?? pageSize,
            data: mapVouchers(data),
          }
        }
        return data as VoucherListResponse
      }

      if ('data' in payload && Array.isArray(payload.data)) {
        return {
          totalRecords: payload.totalRecords ?? payload.data.length,
          pageNumber: payload.pageNumber ?? pageNumber,
          pageSize: payload.pageSize ?? pageSize,
          data: mapVouchers(payload.data),
        }
      }
    }

    if (Array.isArray(payload)) {
      return {
        totalRecords: payload.length,
        pageNumber,
        pageSize,
        data: mapVouchers(payload),
      }
    }

    return {
      totalRecords: payload?.totalRecords ?? 0,
      pageNumber: payload?.pageNumber ?? pageNumber,
      pageSize: payload?.pageSize ?? pageSize,
      data: payload?.data ? mapVouchers(payload.data) : [],
    }
  }

  /**
   * Get voucher by ID
   */
  async getVoucherById(branchId: number, id: number): Promise<Voucher> {
    const url = endpoints.vouchers.getById(id, branchId)
    const response = await apiClient.get<ApiResponse<Voucher> | Voucher>(url)
    
    if ('data' in response.data && response.data.data) {
      return this.mapVoucherData(response.data.data)
    }
    
    return this.mapVoucherData(response.data as any)
  }

  /**
   * Get voucher lines by voucher ID
   */
  async getVoucherLinesByVoucherId(branchId: number, voucherId: number): Promise<VoucherLine[]> {
    const url = endpoints.vouchers.getLinesByVoucherId(voucherId, branchId)
    const response = await apiClient.get<ApiResponse<VoucherLine[]> | VoucherLine[]>(url)
    
    if (Array.isArray(response.data)) {
      return response.data.map(line => this.mapVoucherLine(line))
    }
    if ('data' in response.data && Array.isArray(response.data.data)) {
      return response.data.data.map((line: any) => this.mapVoucherLine(line))
    }
    return []
  }

  /**
   * Get user IP address (logged-in user's IP, not server's IP)
   */
  private async getUserIPAddress(): Promise<string> {
    try {
      const response = await fetch('https://api.ipify.org?format=json')
      const data = await response.json()
      return data.ip || '0.0.0.0'
    } catch (error) {
      console.warn('Failed to get IP address from service, using fallback')
      return '0.0.0.0'
    }
  }

  /**
   * Convert Voucher object to FormData for multipart/form-data submission
   * API expects: VoucherLinesJson as JSON string field
   */
  private voucherToFormData(voucher: Voucher, ipAddress?: string): FormData {
    const formData = new FormData()

    // Add all voucher header fields (matching API structure)
    formData.append('VoucherId', String(voucher.voucherId || 0))
    formData.append('RefNo', voucher.refNo || '')
    // RefType can be empty string (None), 'Customer', or 'Vendor'
    formData.append('RefType', voucher.refType || '')
    formData.append('VoucherEntryDate', voucher.voucherEntryDate || '')
    formData.append('VoucherCode', voucher.voucherCode || '')
    formData.append('Currency', voucher.currency || '')
    formData.append('TotalDebit', String(voucher.totalDebit || 0))
    formData.append('TotalCredit', String(voucher.totalCredit || 0))
    formData.append('Description', voucher.description || '')
    formData.append('CreatedDate', voucher.createdDate || new Date().toISOString())
    formData.append('CreatedBy', String(voucher.createdBy || 0))
    formData.append('ModifiedDate', voucher.modifiedDate || new Date().toISOString())
    formData.append('ModifiedBy', String(voucher.modifiedBy || 0))
    formData.append('VoucherType', String(voucher.voucherType || 0))
    formData.append('CustomerId', String(voucher.customerId || 0))
    formData.append('VendorId', String(voucher.vendorId || 0))
    formData.append('BranchId', String(voucher.branchId || 0))
    formData.append('File', voucher.file || '')
    formData.append('Department', voucher.department || '')
    formData.append('IpAddress', ipAddress || voucher.ipAddress || '0.0.0.0')

    // Add attachment file only if present (don't send empty string - API validation rejects it)
    if (voucher.attachmentFile) {
      formData.append('AttachmentFile', voucher.attachmentFile)
    }
    // Note: We don't send AttachmentFile field at all if no file is present
    // Sending empty string causes validation error from API

    // Prepare voucher lines for JSON serialization
    const voucherLinesToSend = voucher.voucherLines && voucher.voucherLines.length > 0 
      ? voucher.voucherLines.map((line: VoucherLine) => ({
          lineId: line.lineId || 0,
          voucherId: line.voucherId || 0,
          accountId: line.accountId || 0,
          employeeId: line.employeeId || 0,
          description: line.description || '',
          debit: Number(line.debit) || 0,
          credit: Number(line.credit) || 0,
          projectId: line.projectId || 0,
          createdDate: line.createdDate || new Date().toISOString(),
          createdBy: line.createdBy || 0,
          modifiedDate: line.modifiedDate || new Date().toISOString(),
          modifiedBy: line.modifiedBy || 0,
          reportShowStatus: line.reportShowStatus !== undefined ? line.reportShowStatus : true,
          date: line.date || new Date().toISOString(),
          department: line.department || '',
          costProfitCenterId: line.costProfitCenterId || 0,
          costLocationId: line.costLocationId || 0,
          isDeleted: line.isDeleted || 'false',
          branchId: line.branchId || 0,
        }))
      : []

    // Add VoucherLinesJson as JSON string (API expects this field name)
    const voucherLinesJson = JSON.stringify(voucherLinesToSend)
    formData.append('VoucherLinesJson', voucherLinesJson)
    
    // Also add VoucherLines as array (some APIs might need both)
    // But primary field is VoucherLinesJson according to the API structure
    if (voucherLinesToSend.length > 0) {
      formData.append('VoucherLines', voucherLinesJson)
    } else {
      formData.append('VoucherLines', '[]')
    }

    return formData
  }

  /**
   * Create a new voucher
   */
  async createVoucher(voucher: Voucher, ipAddress?: string): Promise<any> {
    const url = endpoints.vouchers.create
    
    // Get IP address if not provided
    const userIpAddress = ipAddress || await this.getUserIPAddress()
    
    // Convert to FormData
    const formData = this.voucherToFormData(voucher, userIpAddress)

    // Detailed FormData logging
    console.group('%c📤 CREATE VOUCHER REQUEST (FormData)', 'background: #4CAF50; color: white; padding: 10px; font-weight: bold; font-size: 16px; border-radius: 5px;')
    console.log('%c🔗 URL:', 'color: #2196F3; font-weight: bold; font-size: 14px;', url)
    console.log('%c🌐 IP Address:', 'color: #FF9800; font-weight: bold; font-size: 14px;', userIpAddress)
    console.log('%c📋 FormData Fields:', 'color: #9C27B0; font-weight: bold; font-size: 14px;')
    
    const formDataObj: Record<string, any> = {}
    for (const [key, value] of formData.entries()) {
      if (key === 'VoucherLines' || key === 'VoucherLinesJson') {
        try {
          formDataObj[key] = JSON.parse(value as string)
        } catch {
          formDataObj[key] = value
        }
      } else if (value instanceof File) {
        formDataObj[key] = `[File: ${value.name}, Size: ${value.size} bytes, Type: ${value.type}]`
      } else {
        formDataObj[key] = value
      }
    }
    console.table(formDataObj)
    console.log('%c📄 VoucherLinesJson (Parsed - Primary Field):', 'color: #E91E63; font-weight: bold; font-size: 14px;')
    if (formDataObj['VoucherLinesJson']) {
      console.table(formDataObj['VoucherLinesJson'])
      console.log('%c📄 VoucherLinesJson (Raw JSON String):', 'color: #9C27B0; font-weight: bold; font-size: 12px;')
      console.log(JSON.stringify(formDataObj['VoucherLinesJson'], null, 2))
    }
    console.log('%c📄 VoucherLines (Parsed):', 'color: #E91E63; font-weight: bold; font-size: 14px;')
    if (formDataObj['VoucherLines']) {
      console.table(formDataObj['VoucherLines'])
    }
    console.log('%c📦 Complete FormData:', 'color: #00BCD4; font-weight: bold; font-size: 14px;')
    console.log(formDataObj)
    console.groupEnd()

    // Send as multipart/form-data
    // Note: Don't set Content-Type header manually - Axios will set it with boundary automatically
    const response = await apiClient.post<ApiResponse<any>>(url, formData)
    return response.data
  }

  /**
   * Update an existing voucher
   */
  async updateVoucher(voucher: Voucher, ipAddress?: string): Promise<any> {
    const url = endpoints.vouchers.update
    
    // Get IP address if not provided
    const userIpAddress = ipAddress || await this.getUserIPAddress()
    
    // Convert to FormData
    const formData = this.voucherToFormData(voucher, userIpAddress)

    // Detailed FormData logging
    console.group('%c📤 UPDATE VOUCHER REQUEST (FormData)', 'background: #FF9800; color: white; padding: 10px; font-weight: bold; font-size: 16px; border-radius: 5px;')
    console.log('%c🔗 URL:', 'color: #2196F3; font-weight: bold; font-size: 14px;', url)
    console.log('%c🌐 IP Address:', 'color: #FF9800; font-weight: bold; font-size: 14px;', userIpAddress)
    console.log('%c📋 FormData Fields:', 'color: #9C27B0; font-weight: bold; font-size: 14px;')
    
    const formDataObj: Record<string, any> = {}
    for (const [key, value] of formData.entries()) {
      if (key === 'VoucherLines' || key === 'VoucherLinesJson') {
        try {
          formDataObj[key] = JSON.parse(value as string)
        } catch {
          formDataObj[key] = value
        }
      } else if (value instanceof File) {
        formDataObj[key] = `[File: ${value.name}, Size: ${value.size} bytes, Type: ${value.type}]`
      } else {
        formDataObj[key] = value
      }
    }
    console.table(formDataObj)
    console.log('%c📄 VoucherLinesJson (Parsed - Primary Field):', 'color: #E91E63; font-weight: bold; font-size: 14px;')
    if (formDataObj['VoucherLinesJson']) {
      console.table(formDataObj['VoucherLinesJson'])
      console.log('%c📄 VoucherLinesJson (Raw JSON String):', 'color: #9C27B0; font-weight: bold; font-size: 12px;')
      console.log(JSON.stringify(formDataObj['VoucherLinesJson'], null, 2))
    }
    console.log('%c📄 VoucherLines (Parsed):', 'color: #E91E63; font-weight: bold; font-size: 14px;')
    if (formDataObj['VoucherLines']) {
      console.table(formDataObj['VoucherLines'])
    }
    console.log('%c📦 Complete FormData:', 'color: #00BCD4; font-weight: bold; font-size: 14px;')
    console.log(formDataObj)
    console.groupEnd()

    // Send as multipart/form-data
    // Note: Don't set Content-Type header manually - Axios will set it with boundary automatically
    const response = await apiClient.post<ApiResponse<any>>(url, formData)
    return response.data
  }

  /**
   * Create a reversing voucher for a manually created voucher
   */
  async reverseVoucher(id: number, branchId: number): Promise<any> {
    const url = endpoints.vouchers.reverse(id, branchId)
    const response = await apiClient.post<ApiResponse<any>>(url)
    return response.data
  }

  /**
   * Map voucher list item data
   */
  private mapVoucherListItem(data: any): VoucherListItem {
    return {
      id: data.id || data.voucherId || 0,
      voucherId: data.voucherId || data.id || 0,
      voucherCode: data.voucherCode || data.voucherEntryCode || '',
      voucherType: data.voucherType || data.generalEntryType || '',
      voucherEntryDate: data.voucherEntryDate || data.generalEntryDate || '',
      totalDebit: data.totalDebit || 0,
      totalCredit: data.totalCredit || 0,
      isPosted: data.isPosted || false,
      isManualVoucher: data.isManualVoucher ?? data.IsManualVoucher ?? false,
      isReversed: data.isReversed ?? data.IsReversed ?? false,
      isReversal: data.isReversal ?? data.IsReversal ?? false,
      reversalOfVoucherId: data.reversalOfVoucherId ?? data.ReversalOfVoucherId ?? null,
      refNo: data.refNo || data.RefNo || '',
      refType: data.refType || data.RefType || '',
      customerName: data.customerName || '',
      vendorName: data.vendorName || '',
    }
  }

  /**
   * Map voucher data
   */
  private mapVoucherData(data: any): Voucher {
    return {
      voucherId: data.voucherId || data.VoucherId || 0,
      refNo: data.refNo || data.RefNo || '',
      refType: data.refType || data.RefType || '',
      voucherEntryDate: data.voucherEntryDate || data.generalEntryDate || '',
      voucherCode: data.voucherCode || data.generalEntryCode || '',
      currency: data.currency || '',
      totalDebit: data.totalDebit || 0,
      totalCredit: data.totalCredit || 0,
      description: data.description || '',
      createdDate: data.createdDate || '',
      createdBy: data.createdBy || 0,
      modifiedDate: data.modifiedDate || '',
      modifiedBy: data.modifiedBy || 0,
      voucherType: data.voucherType || 0,
      customerId: data.customerId || data.CustomerId || 0,
      vendorId: data.vendorId || data.VendorId || 0,
      branchId: data.branchId || 0,
      file: data.file || '',
      department: data.department || '',
      isManualVoucher: data.isManualVoucher ?? data.IsManualVoucher ?? false,
      isReversed: data.isReversed ?? data.IsReversed ?? false,
      isReversal: data.isReversal ?? data.IsReversal ?? false,
      reversalOfVoucherId: data.reversalOfVoucherId ?? data.ReversalOfVoucherId ?? null,
      voucherLines: Array.isArray(data.voucherLines) 
        ? data.voucherLines.map((line: any) => this.mapVoucherLine(line))
        : [],
    }
  }

  /**
   * Map voucher line data
   */
  private mapVoucherLine(data: any): VoucherLine {
    return {
      lineId: data.lineId || 0,
      voucherId: data.voucherId || 0,
      accountId: data.accountId || 0,
      employeeId: data.employeeId || 0,
      description: data.description || '',
      debit: data.debit || 0,
      credit: data.credit || 0,
      projectId: data.projectId || 0,
      createdDate: data.createdDate || '',
      createdBy: data.createdBy || 0,
      modifiedDate: data.modifiedDate || '',
      modifiedBy: data.modifiedBy || 0,
      reportShowStatus: data.reportShowStatus || true,
      date: data.date || '',
      department: data.department || '',
      costProfitCenterId: data.costProfitCenterId || 0,
      costLocationId: data.costLocationId || 0,
      isDeleted: data.isDeleted || '',
      branchId: data.branchId || 0,
    }
  }
}

export const voucherService = new VoucherService()
